import { QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider } from "@tanstack/react-router"
import { useEffect } from "react"

import { createAppRouter } from "@/app/router"
import { createQueryClient } from "@/app/query-client"
import { ServiceUnavailable } from "@/components/layout/service-unavailable"
import { ThemeProvider } from "@/components/theme-provider"
import {
  setOrganisationAccessDeniedHandler,
  setUnauthenticatedHandler,
} from "@/lib/api/client"
import { apiKey } from "@/lib/api/changes"
import { setRecoveryHandler } from "@/lib/api/outage"
import { onAuthChange } from "@/lib/api/session"
import { recordPortalReplays } from "@/lib/sentry"
import { signInPath } from "@/lib/sign-in-path"

const queryClient = createQueryClient()
const router = createAppRouter(queryClient)

/**
 * The API client reports these rather than importing the router itself, which
 * keeps the transport layer free of routing.
 */
setUnauthenticatedHandler(() => {
  queryClient.clear()
  void router.navigate({
    to: signInPath(router.state.location.pathname),
    replace: true,
  })
})

setOrganisationAccessDeniedHandler(() => {
  queryClient.clear()
  void queryClient.invalidateQueries({ queryKey: apiKey("/auth/session") })
  void router.navigate({ to: "/organisations/select", replace: true })
})

setRecoveryHandler(() => {
  void queryClient.invalidateQueries()
  void router.invalidate()
})

recordPortalReplays(router)

function Providers() {
  useEffect(() => {
    /**
     * Signing in or out in one tab has to take effect in the others, or a
     * visitor ends up looking at a signed-in page they no longer have a session
     * for.
     */
    return onAuthChange(() => {
      queryClient.clear()
      void router.invalidate()
    })
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <RouterProvider router={router} />
        <ServiceUnavailable />
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export { Providers }
